import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {UserHomepageStateController, UserHomepageView} from "../../../../../containers/user-homepage";
import {UserHomepageControllerEvents, UserHomePageTypes, UserHomepageViewMode, UserHomepageWebPreviewViewportWidth} from "../../../../../containers/user-homepage/models";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import UserPlatformPreviewNavigation from "../../../../../components/app-specific/single-edition-homepage/sections/preview-navigation";
import {Paper} from "@mui/material";
import classnames from "classnames";
import {Col} from "reactstrap";
import {createUUId} from "../../../../../../core/services/utils/utils";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import {HomepagePlatforms} from "../../../../../../core/constants/enums";
import axios from "axios";
import Api from "../../../../../../core/services/api/api";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useQuery from "../../../../../hooks/use-query";
import CustomerMemberAutocomplete from "../../../../../components/text-fields/autocomplete/customer-member-autocomplete";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";

const tabs = Object.entries(UserHomePageTypes).map(e => {
    switch (e[1]) {
        case UserHomePageTypes.phone:
            return {
                title: "Application's home page",
                value: e[1],
            };
        case UserHomePageTypes.web:
            return {
                title: "Website's home page",
                value: e[1],
            };
        default:
            return {};
    }
})

const UserPlatformLiveHomepagesView = () => {
    const [query, setQuery] = useQuery();
    const [mainPageExists, setMainPageExists] = useState(false)
    const stateControllerRef = useRef();
    const isMounted = useIsMounted();

    const viewType = useMemo(() => query?.tab ?? tabs[0].value, [query]);
    const selectedUser = useMemo(() => query?.user ? JSON.parse(query.user) : null, [query]);

    const bodyIdRef = useRef(createUUId(true));
    const deviceIdRef = useRef(createUUId(true));
    const deviceContainerIdRef = useRef(createUUId(true));

    const {configureNotebookTags} = useConfigureNotebook('user-platform-live-homepages-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.userPlatformLiveHomepages);
        configureBehavioralTool((tags) => tags.userPlatformLiveHomepages);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches the given tab' information from the server and populates the state of the controller.
     * @type {function(UserHomepageEventEmitter, number): Promise<void>}
     */
    const getTabInformation = useCallback(async (_, tabId) => {
        const forApi = {
            platformId: viewType === UserHomePageTypes.web ? HomepagePlatforms.web : HomepagePlatforms.phone,
            tabId: tabId,
            userId: selectedUser?.id,
        }
        const response = await Api.getLiveUserHomepageData(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setMainPageExists(true);
            const newTab = response?.data?.find(e => e.id === tabId);
            if (!newTab)
                return;
            stateControllerRef.current?.controller?.loadTab(newTab, response.configuration);
        } else {
            setMainPageExists(false);
        }
    }, [stateControllerRef, viewType, selectedUser, isMounted])

    /**
     * With each change in the controller or state:
     * - attaches an event listener for the selection of tabs so that the tab information can be loaded from the
     */
    useEffect(() => {
        if (!selectedUser)
            return;
        stateControllerRef.current?.controller?.on(UserHomepageControllerEvents.onUnloadedTabSelected, getTabInformation);
        const cleanup = () => {
            stateControllerRef.current?.controller?.off(UserHomepageControllerEvents.onUnloadedTabSelected, getTabInformation)
        }
        return () => {
            cleanup();
        }
    }, [stateControllerRef, selectedUser, getTabInformation])


    /**
     * Fetches the main page data for the current edition of the user homepage from the server.
     * @return {Promise<void>}
     */
    const getMainPageData = useCallback(async (cancelToken) => {
        const forApi = {
            platformId: viewType === UserHomePageTypes.web ? HomepagePlatforms.web : HomepagePlatforms.phone,
            tabId: null,
            userId: selectedUser?.id,
        }
        stateControllerRef.current?.controller?.resetState();
        const response = await Api.getLiveUserHomepageData(forApi, cancelToken.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            stateControllerRef.current?.controller?.loadHomepage(
                {
                    title: '',
                    tabs: response?.data ?? [],
                },
                response.configuration
            );
        }
    }, [stateControllerRef, viewType, selectedUser, isMounted])


    /**
     * With each change in the view type and selected user of this component:
     * - loads the main-page data from the server only if there is a selected user.
     */
    useEffect(() => {
        if (!selectedUser)
            return;
        const axiosCancelToken = axios.CancelToken.source();
        getMainPageData(axiosCancelToken).then();
        return () => axiosCancelToken.cancel();
    }, [viewType, selectedUser, getMainPageData])

    /**
     * Changes the current view type of the page.
     * @type {function(MouseEvent, string): void}
     */
    const onTabChange = useCallback((_, newValue) => {
        setQuery(prevState => ({...prevState, tab: newValue}))
    }, [setQuery])

    /**
     * Changes the selected user with the given user information.
     * @type {function(*=, *): void}
     */
    const onNewUserSelected = useCallback((_, user) => {
        setQuery(prevState => ({...prevState, user: user ? JSON.stringify(user) : undefined}))
    }, [setQuery])

    return (
        <>
            <UserHomepageStateController ref={stateControllerRef} viewMode={UserHomepageViewMode.preview}>
                <div id={bodyIdRef.current} className='user-homepage-view live'>
                    <Col xs={12} className={'my-3 px-0'}>
                        <p className={'text-lg font-weight-bold'}>
                            Clients' Live Homepages
                        </p>
                    </Col>
                    <Paper className='paper w-100 px-3 py-2 flex-wrap justify-content-start align-items-start'>
                        <Col xs={12} className={'mb-3 mt-3'}>
                            <CustomerMemberAutocomplete
                                label={'User'}
                                value={selectedUser}
                                onChange={onNewUserSelected}
                            />
                        </Col>
                    </Paper>
                    {
                        !!selectedUser &&
                        <Paper className='paper w-100 px-3 py-2 flex-wrap justify-content-start align-items-start mt-4'>
                            <div className='w-100 flex-grow-1'>
                                <Tabs value={viewType} onChange={onTabChange} aria-label="search tabs">
                                    {
                                        tabs?.map(e => (
                                            <Tab key={e.value} value={e.value} label={e.title}/>
                                        ))
                                    }
                                </Tabs>
                                <div className='my-4 w-100'/>
                            </div>
                            <Col
                                xs={12}
                                id={deviceContainerIdRef.current}
                                className={classnames('homepage-platform order-1 order-lg-2')}
                            >
                                {
                                    mainPageExists &&
                                    <UserHomepageView
                                        bodyId={bodyIdRef.current}
                                        deviceId={deviceIdRef.current}
                                        containerId={deviceContainerIdRef.current}
                                        viewType={viewType}
                                        viewportWidth={viewType === UserHomePageTypes.web ? UserHomepageWebPreviewViewportWidth : 'xxl'}
                                        boundedHeight={viewType !== UserHomePageTypes.web}
                                    />
                                }
                                {
                                    !mainPageExists &&
                                    <p className={'text-center w-100 text-sm px-3 my-5 py-5'}>
                                        No homepage found for this user and the selected platform.
                                    </p>
                                }
                            </Col>
                        </Paper>
                    }
                    {
                        !selectedUser &&
                        <Paper className='paper w-100 px-3 py-5 flex-wrap justify-content-start align-items-start mt-4'>
                            <p className={'text-center text-sm w-100'}>
                                Please select a user to view their live homepage.
                            </p>
                        </Paper>
                    }
                </div>
                <UserPlatformPreviewNavigation/>
            </UserHomepageStateController>
        </>
    )
}


export default UserPlatformLiveHomepagesView;
